// 全局聊天管理器
class ChatManager {
  constructor() {
    this.channels = new Map()
    this.messageHandlers = new Map()
    this.supabase = null
    // 全局消息存储（所有频道的消息）
    this.messages = new Map()
    // 跟踪当前打开的聊天窗口
    this.openChatWindows = new Set()
    // 全局通知处理器
    this.notificationHandler = null
  }

  init(supabaseClient) {
    this.supabase = supabaseClient
  }

  // 标记订单的聊天窗口已打开
  setChatWindowOpen(orderId) {
    console.log(`[ChatManager] 聊天窗口打开: ${orderId}`)
    this.openChatWindows.add(orderId)
  }

  // 标记订单的聊天窗口已关闭
  setChatWindowClosed(orderId) {
    console.log(`[ChatManager] 聊天窗口关闭: ${orderId}`)
    this.openChatWindows.delete(orderId)
  }

  // 检查订单的聊天窗口是否打开
  isChatWindowOpen(orderId) {
    return this.openChatWindows.has(orderId)
  }

  // 设置全局通知处理器
  setNotificationHandler(handler) {
    this.notificationHandler = handler
  }

  // 触发全局通知（使用 window 事件确保可靠性）
  triggerNotification(orderId, message) {
    console.log('[ChatManager] 触发通知:', orderId, message)
    if (this.notificationHandler) {
      this.notificationHandler(orderId, message)
    }
    window.dispatchEvent(new CustomEvent('chat-notification', {
      detail: { orderId, message }
    }))
  }

  // 根据订单ID生成频道名
  getChannelName(orderId) {
    // 检查空值
    if (!orderId) {
      console.error('[ChatManager] 无效的订单ID:', orderId)
      return null
    }
    
    const idStr = String(orderId)
    
    if (idStr.startsWith('xianyu_')) {
      return `order-chat-${idStr}`
    }
    
    if (idStr.startsWith('orders_')) {
      return `order-chat-${idStr}`
    }
    
    return `order-chat-${idStr}`
  }

  // 获取订单的消息存储key
  getStorageKey(orderId) {
    if (!orderId) {
      console.error('[ChatManager] getStorageKey: 无效的订单ID')
      return null
    }
    return `chat-messages-${orderId}`
  }

  // 从本地存储加载消息
  loadMessages(orderId) {
    // 检查空值
    if (!orderId) {
      console.error('[ChatManager] 无法加载消息: 订单ID为空')
      return []
    }
    
    try {
      const saved = localStorage.getItem(this.getStorageKey(orderId))
      if (saved) {
        const messages = JSON.parse(saved)
        this.messages.set(orderId, messages)
        console.log(`[ChatManager] 加载订单 ${orderId} 的 ${messages.length} 条历史消息`)
        return messages
      }
    } catch (e) {
      console.error(`[ChatManager] 加载订单 ${orderId} 消息失败:`, e)
    }
    // 确保设置一个空数组
    if (!this.messages.has(orderId)) {
      this.messages.set(orderId, [])
    }
    return []
  }

  // 保存消息到本地存储
  saveMessages(orderId) {
    if (!orderId) {
      console.error('[ChatManager] 无法保存消息: 订单ID为空')
      return
    }
    
    const storageKey = this.getStorageKey(orderId)
    if (!storageKey) {
      return
    }
    
    try {
      const messages = this.messages.get(orderId) || []
      localStorage.setItem(storageKey, JSON.stringify(messages))
    } catch (e) {
      console.error(`[ChatManager] 保存订单 ${orderId} 消息失败:`, e)
    }
  }

  // 添加消息
  addMessage(orderId, message) {
    if (!orderId) {
      console.error('[ChatManager] 无法添加消息: 订单ID为空')
      return
    }
    
    // 先确保这个订单已经加载过消息
    if (!this.messages.has(orderId)) {
      this.loadMessages(orderId)
    }
    
    const messages = this.messages.get(orderId) || []
    
    // 检查是否已经存在这条消息（避免重复）
    const exists = messages.some(
      msg => msg.timestamp === message.timestamp && msg.text === message.text
    )
    
    if (!exists) {
      messages.push(message)
      this.messages.set(orderId, messages)
      this.saveMessages(orderId)
      console.log(`[ChatManager] 保存消息到订单 ${orderId}`)
      
      // 如果聊天窗口没打开且有通知处理器，触发通知
      if (!this.isChatWindowOpen(orderId)) {
        // 只有对方的消息才显示通知
        if (message.sender !== 'player') {
          console.log(`[ChatManager] 显示通知: 订单 ${orderId}`)
          this.triggerNotification(orderId, message)
        }
      }
    }
  }

  // 获取订单的消息
  getMessages(orderId) {
    if (!orderId) {
      console.error('[ChatManager] getMessages: 无效的订单ID')
      return []
    }
    
    // 如果消息还没加载，先加载
    if (!this.messages.has(orderId)) {
      this.loadMessages(orderId)
    }
    
    return this.messages.get(orderId) || []
  }

  getChannel(orderId) {
    if (!orderId) {
      console.error('[ChatManager] getChannel: 无效的订单ID')
      return null
    }
    
    const channelName = this.getChannelName(orderId)
    if (!channelName) {
      console.error('[ChatManager] getChannel: 无效的频道名')
      return null
    }

    if (this.channels.has(channelName)) {
      console.log(`[ChatManager] 复用已有频道: ${channelName}`)
      return this.channels.get(channelName)
    }

    console.log(`[ChatManager] 创建新频道: ${channelName}`)

    const channel = this.supabase.channel(channelName)
    this.channels.set(channelName, channel)

    return channel
  }

  subscribe(orderId, onMessage) {
    // 检查空值
    if (!orderId) {
      console.error('[ChatManager] 无法订阅: 订单ID为空')
      return
    }
    
    const channelName = this.getChannelName(orderId)
    if (!channelName) {
      console.error('[ChatManager] 无法订阅: 频道名为空')
      return
    }
    
    const channel = this.getChannel(orderId)

    // 先加载历史消息
    this.loadMessages(orderId)

    // 每个订阅都有独立的处理器
    console.log(`[ChatManager] 开始订阅: ${channelName}`)

    // 订阅频道
    let retryCount = 0
    const maxRetries = 3

    const doSubscribe = () => {
      channel
        .on('broadcast', { event: 'chat-message' }, (payload) => {
          console.log(`[ChatManager] 收到广播消息:`, payload)
          const message = payload.payload
          // 全局保存消息（即使组件没打开也保存）
          this.addMessage(orderId, message)
          // 通知组件（如果有回调）
          if (onMessage) {
            onMessage(message)
          }
        })
        .subscribe((status, err) => {
          console.log(`[ChatManager] ${channelName} 订阅状态: ${status}`, err || '')
          if (status === 'SUBSCRIBED') {
            retryCount = 0
            console.log(`[ChatManager] ${channelName} 订阅成功`)
          } else if (status === 'CHANNEL_ERROR') {
            console.error(`[ChatManager] ${channelName} 订阅失败:`, err)
            // 重试机制
            if (retryCount < maxRetries) {
              retryCount++
              console.log(`[ChatManager] ${channelName} 尝试重新订阅 (${retryCount}/${maxRetries})`)
              setTimeout(() => {
                doSubscribe()
              }, 2000 * retryCount)
            } else {
              console.error(`[ChatManager] ${channelName} 订阅失败，已达到最大重试次数`)
            }
          } else if (status === 'CLOSED') {
            console.log(`[ChatManager] ${channelName} 已关闭`)
          }
        })
    }

    doSubscribe()
  }

  async sendMessage(orderId, message) {
    // 检查空值
    if (!orderId) {
      console.error('[ChatManager] 无法发送消息: 订单ID为空')
      return { error: '订单ID无效' }
    }
    
    const channelName = this.getChannelName(orderId)
    if (!channelName) {
      console.error('[ChatManager] 无法发送消息: 频道名为空')
      return { error: '频道无效' }
    }
    
    const channel = this.channels.get(channelName)

    if (!channel) {
      console.error(`[ChatManager] 频道 ${channelName} 不存在，尝试先订阅`)
      // 先订阅再发送
      this.subscribe(orderId, null)
      // 等待一下让订阅完成
      await new Promise(resolve => setTimeout(resolve, 500))
      const channelAfter = this.channels.get(channelName)
      if (!channelAfter) {
        return { error: '频道不存在' }
      }
      // 先保存到本地
      this.addMessage(orderId, message)
      return await channelAfter.send({
        type: 'broadcast',
        event: 'chat-message',
        payload: message
      })
    }

    console.log(`[ChatManager] 发送消息到 ${channelName}:`, message)

    // 先保存到本地
    this.addMessage(orderId, message)

    return await channel.send({
      type: 'broadcast',
      event: 'chat-message',
      payload: message
    })
  }

  async unsubscribe(orderId) {
    if (!orderId) {
      console.error('[ChatManager] 无法取消订阅: 订单ID为空')
      return
    }
    
    const channelName = this.getChannelName(orderId)
    if (!channelName) {
      return
    }
    
    if (this.channels.has(channelName)) {
      console.log(`[ChatManager] 移除频道: ${channelName}`)
      await this.supabase.removeChannel(this.channels.get(channelName))
      this.channels.delete(channelName)
    }
  }

  getActiveChannels() {
    return Array.from(this.channels.keys())
  }

  // 清理订单的所有聊天资源
  cleanupOrder(orderId) {
    console.log(`[ChatManager] 清理订单 ${orderId} 的聊天资源`)
    
    // 取消订阅频道
    this.unsubscribe(orderId)
    
    // 从内存中移除消息（保留本地存储，用户可能还想查看历史记录）
    // 注意：不移除 localStorage 的数据，用户可能想查看历史消息
    
    console.log(`[ChatManager] 订单 ${orderId} 聊天资源清理完成`)
  }

  // 批量清理多个订单的聊天资源
  cleanupOrders(orderIds) {
    orderIds.forEach(orderId => {
      this.cleanupOrder(orderId)
    })
  }

  // 获取当前活跃的订单列表
  getActiveOrders() {
    return Array.from(this.messages.keys())
  }

  // 清理过期的消息缓存（保留最近N天的消息）
  cleanupOldMessages(daysToKeep = 7) {
    const cutoffTime = new Date()
    cutoffTime.setDate(cutoffTime.getDate() - daysToKeep)
    const cutoffTimestamp = cutoffTime.getTime()

    let cleanedCount = 0
    
    this.messages.forEach((messages, orderId) => {
      const recentMessages = messages.filter(msg => {
        const msgTime = new Date(msg.timestamp).getTime()
        return msgTime > cutoffTimestamp
      })
      
      if (recentMessages.length < messages.length) {
        this.messages.set(orderId, recentMessages)
        this.saveMessages(orderId)
        cleanedCount++
      }
    })
    
    console.log(`[ChatManager] 清理了 ${cleanedCount} 个订单的过期消息`)
  }
}

export const chatManager = new ChatManager()
export default chatManager